import { useState } from "react";
import { useAuthProvider, useNotify, useTranslate } from "react-admin";
import {
  Box,
  Button,
  Link as MuiLink,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import useStyles from "./style";
import { usePageTitle } from "@/utilities";
import type { GameooAuthProvider } from "@/providers/authProvider";

const ForgotPasswordPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const notify = useNotify();
  const translate = useTranslate();

  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const authProvider = useAuthProvider<GameooAuthProvider>();

  usePageTitle(translate("ra.gameoo.signIn.forgotPassword.title"));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!authProvider) {
      notify(
        translate("ra.gameoo.signIn.forgotPassword.authProviderUnavailable"),
        {
          type: "error",
        },
      );
      return;
    }

    setIsSubmitting(true);

    try {
      await authProvider.forgotPassword({ email });
      setIsSent(true);
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Une erreur est survenue.",
        { type: "warning" },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.leftPane}>
        <Box
          component="img"
          src="/logo-gameoo.png"
          alt="GAMEOO"
          className={classes.brand}
        />

        <div className={classes.formWrap}>
          <div className={classes.form}>
            <Typography variant="h4" className={classes.title}>
              {translate("ra.gameoo.signIn.forgotPassword.title")}
            </Typography>

            {isSent ? (
              <>
                <Typography sx={{ mb: 3 }}>
                  {translate("ra.gameoo.signIn.forgotPassword.successMessage")}
                </Typography>

                <Button
                  className={classes.buttonLogIn}
                  variant="contained"
                  onClick={() => navigate("/login")}
                >
                  {translate("ra.gameoo.signIn.forgotPassword.backToLogin")}
                </Button>
              </>
            ) : (
              <form onSubmit={handleSubmit}>
                <Typography sx={{ mb: 3 }}>
                  {translate("ra.gameoo.signIn.forgotPassword.instruction")}
                </Typography>

                <TextField
                  className={classes.input}
                  label={translate(
                    "ra.gameoo.signIn.forgotPassword.emailLabel",
                  )}
                  type="email"
                  value={email}
                  required
                  fullWidth
                  autoComplete="email"
                  onChange={(event) => setEmail(event.target.value)}
                  sx={{ mb: 2 }}
                />

                <Button
                  className={classes.buttonLogIn}
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? translate("ra.gameoo.signIn.forgotPassword.submitting")
                    : translate(
                        "ra.gameoo.signIn.forgotPassword.sendButtonLabel",
                      )}
                </Button>

                <Typography sx={{ mt: 2, textAlign: "center" }}>
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={() => navigate("/login")}
                  >
                    {translate("ra.gameoo.signIn.forgotPassword.backToLogin")}
                  </MuiLink>
                </Typography>
              </form>
            )}
          </div>
        </div>
      </div>

      <div className={classes.rightPane} aria-hidden />
    </div>
  );
};

export default ForgotPasswordPage;
